// src/components/layout/FleetAppShell.jsx — Fleet portal layout.
// macOS-style frosted sidebar, grouped nav (Navigation / Work / Master / Insights),
// Day/Night theme via CSS variables, drawer on phones.
import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import useIsMobile from '../../pages/fleet/useIsMobile';
import Icon, { IconName } from '../../pages/fleet/Icon';
import { css } from '../../pages/fleet/fleetStyles';
import { THEMES, FONT, useFleetTheme, ThemeMode } from '../../pages/fleet/theme';
import { MAP_CSS } from '../../pages/fleet/mapKit';
import { permissionsOf } from '../../pages/fleet/permissions';
import type { FleetPermission } from '../../types';

// `perm` is optional: an entry without one (Helpdesk) is open to anyone signed in.
interface NavEntry { to: string; icon: IconName; label: string; tint: string; end?: boolean; perm?: FleetPermission }
interface NavGroup { key: string; icon: IconName; label: string; tint: string; base: string; children: NavEntry[] }
interface NavSection { title: string; items?: NavEntry[]; group?: NavGroup }

const SECTIONS: NavSection[] = [
  { title: 'Navigation', items: [
    { to: '/fleet/dashboard', icon: 'map', label: 'Dashboard', tint: '#0A84FF', perm: 'dashboard' },
  ] },
  { title: 'Work', items: [
    { to: '/fleet/trips/new', icon: 'plus', label: 'Create Trip', tint: '#34C759', perm: 'create_trip' },
    { to: '/fleet/trips',     icon: 'list', label: 'Trip List', tint: '#FF9500', end: true, perm: 'trip_list' },
    { to: '/fleet/ifms',      icon: 'shield', label: 'IFMS Queue', tint: '#FF2D55', perm: 'ifms' },
  ] },
  { title: 'Master', group: {
    key: 'master', icon: 'database', label: 'Master', tint: '#5856D6', base: '/fleet/master',
    children: [
      { to: '/fleet/master/assets',    icon: 'antenna', label: 'Assets',   tint: '#30B0C7', perm: 'master' },
      { to: '/fleet/master/vehicles',  icon: 'truck',   label: 'Vehicle',  tint: '#007AFF', perm: 'master' },
      { to: '/fleet/master/vendors',   icon: 'users',   label: 'Vendor',   tint: '#30B0C7', perm: 'master' },
      { to: '/fleet/master/states',    icon: 'globe',   label: 'State',    tint: '#34C759', perm: 'master' },
      { to: '/fleet/master/districts', icon: 'pin',     label: 'District', tint: '#FF3B30', perm: 'master' },
    ],
  } },
  { title: 'Insights', items: [
    { to: '/fleet/alerts',  icon: 'bell',  label: 'Alerts',  tint: '#FF3B30', perm: 'alerts' },
  ], group: {
    key: 'reports', icon: 'chart', label: 'Reports', tint: '#AF52DE', base: '/fleet/reports',
    children: [
      { to: '/fleet/reports',           icon: 'chart',   label: 'Overview',         tint: '#AF52DE', perm: 'reports', end: true },
      { to: '/fleet/reports/performance', icon: 'clock', label: 'Trip Performance', tint: '#34C759', perm: 'reports' },
      { to: '/fleet/reports/trips',     icon: 'list',    label: 'Trip Report',      tint: '#FF9500', perm: 'reports' },
      { to: '/fleet/reports/vehicles',  icon: 'truck',   label: 'Vehicle Summary',  tint: '#007AFF', perm: 'reports' },
      { to: '/fleet/reports/stoppages', icon: 'pin',     label: 'Stoppage',         tint: '#FF3B30', perm: 'reports' },
      { to: '/fleet/reports/overspeed', icon: 'chart',   label: 'Overspeed',        tint: '#AF52DE', perm: 'reports' },
      { to: '/fleet/reports/alerts',    icon: 'bell',    label: 'Alert Report',     tint: '#FF2D55', perm: 'reports' },
      { to: '/fleet/reports/devices',   icon: 'antenna', label: 'Device Health',    tint: '#30B0C7', perm: 'reports' },
    ],
  } },
  { title: 'Support', items: [
    { to: '/fleet/helpdesk', icon: 'lifebuoy', label: 'Helpdesk', tint: '#0A84FF' },
  ] },
  { title: 'Administration', items: [
    { to: '/fleet/admin/roles', icon: 'shield', label: 'Roles', tint: '#8E8E93', perm: 'admin' },
    { to: '/fleet/admin/users', icon: 'users',  label: 'Users', tint: '#5AC8FA', perm: 'admin' },
    { to: '/fleet/admin/ifms',  icon: 'shield',  label: 'IFMS',  tint: '#30B0C7', perm: 'ifms' },
    { to: '/fleet/admin/providers', icon: 'antenna', label: 'GPS Providers', tint: '#0A84FF', perm: 'admin' },
  ] },
];

// Drop entries the signed-in user's roles do not grant, then empty sections.
function visibleSections(perms: FleetPermission[]): NavSection[] {
  const ok = (n: NavEntry) => !n.perm || perms.includes(n.perm);
  return SECTIONS.flatMap<NavSection>((sec) => {
    const items = (sec.items || []).filter(ok);
    const children = sec.group ? sec.group.children.filter(ok) : [];
    const group = sec.group && children.length ? { ...sec.group, children } : undefined;
    return items.length || group ? [{ ...sec, items, group }] : [];
  });
}

export default function FleetAppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const [mode, setMode] = useFleetTheme();
  const [open, setOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>(() => ({ master: pathname.startsWith('/fleet/master'), reports: pathname.startsWith('/fleet/reports') }));
  const doLogout = () => { logout(); navigate('/fleet/login'); };
  const closeM = () => { if (isMobile) setOpen(false); };
  const { colorScheme, ...vars } = THEMES[mode];
  const sections = visibleSections(permissionsOf(user));
  const roleLabel = user?.role === 'FLEET_USER'
    ? (user.fleetRoles || []).map((r) => r.name).join(', ') || 'No role'
    : (user?.role || '').replace(/_/g, ' ').toLowerCase();

  const tile = (icon: IconName, tint: string, sub = false) => (
    <span style={{ ...S.tile, ...(sub ? S.tileSm : {}), background: `linear-gradient(180deg, ${tint}E6 0%, ${tint} 100%)` }}>
      <Icon name={icon} size={sub ? 13 : 15} stroke={2.1} />
    </span>
  );
  const item = (n: NavEntry, sub = false) => (
    <NavLink key={n.to} to={n.to} end={!!n.end} onClick={closeM}
      style={({ isActive }) => ({ ...S.item, ...(sub ? S.subItem : {}), ...(isActive ? S.itemOn : {}) })}>
      {tile(n.icon, n.tint, sub)}
      <span style={{ flex: 1 }}>{n.label}</span>
    </NavLink>
  );

  const themeSwitch = (
    <div style={S.seg} role="group" aria-label="Theme">
      {([['light', 'sun', 'Day'], ['dark', 'moon', 'Night']] as [ThemeMode, IconName, string][]).map(([m, ic, lbl]) => (
        <button key={m} type="button" onClick={() => setMode(m)} aria-pressed={mode === m}
          style={{ ...S.segBtn, ...(mode === m ? S.segOn : {}) }}>
          <Icon name={ic} size={14} /> {lbl}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ ...S.wrap, ...vars, colorScheme }} data-theme={mode}>
      <style>{GLOBAL_CSS + MAP_CSS}</style>
      {isMobile && (
        <div style={S.topbar}>
          <button style={S.iconBtn} onClick={() => setOpen(true)} aria-label="Open menu"><Icon name="menu" size={18} /></button>
          <span style={S.topName}>Fleet</span>
          <button style={S.iconBtn} onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')} aria-label="Toggle theme">
            <Icon name={mode === 'dark' ? 'sun' : 'moon'} size={17} />
          </button>
        </div>
      )}
      {isMobile && open && <div style={S.overlay} onClick={() => setOpen(false)} />}

      <aside style={{ ...S.side, ...(isMobile ? { position: 'fixed', top: 0, left: 0, zIndex: 60, transform: open ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.28s cubic-bezier(.2,.8,.2,1)', boxShadow: open ? 'var(--fx-shadow-lg)' : 'none' } : {}) }}>
        <div style={S.brand}>
          {/* alt="" on purpose: the brand name sits right beside it, and a
              screen reader announcing "Fertilizer Tracking" twice is worse
              than announcing it once. */}
          <div style={S.appIcon}><img src="/logo-mark.svg" alt="" width={34} height={34} style={S.appIconImg} /></div>
          <div style={{ minWidth: 0 }}>
            <div style={S.brandName}>Fertilizer Tracking</div>
            <div style={S.brandSub}>Fleet · GPS Tracking</div>
          </div>
          {isMobile && <button style={{ ...S.iconBtn, marginLeft: 'auto' }} onClick={() => setOpen(false)} aria-label="Close menu"><Icon name="close" size={16} /></button>}
        </div>

        <nav style={S.nav}>
          {sections.map((sec) => (
            <div key={sec.title} style={{ marginBottom: 6 }}>
              <div style={S.secTitle}>{sec.title}</div>
              {sec.items && sec.items.map((n) => item(n))}
              {sec.group && (() => {
                const g = sec.group;
                const inGroup = pathname.startsWith(g.base);
                const isOpen = groupOpen[g.key] || inGroup;
                return (
                  <>
                    <button type="button" onClick={() => setGroupOpen((o) => ({ ...o, [g.key]: !isOpen }))}
                      style={{ ...S.item, ...S.groupBtn, ...(inGroup ? { color: 'var(--fx-text)', fontWeight: 600 } : {}) }}>
                      {tile(g.icon, g.tint)}
                      <span style={{ flex: 1 }}>{g.label}</span>
                      <Icon name="chevron" size={15} style={{ color: 'var(--fx-text-3)', transition: 'transform .2s', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                    </button>
                    {isOpen && <div style={S.subWrap}>{g.children.map((c) => item(c, true))}</div>}
                  </>
                );
              })()}
            </div>
          ))}
        </nav>

        <div style={S.bottom}>
          {themeSwitch}
          <Link to="/" style={S.back} onClick={closeM}><Icon name="back" size={14} /> All Services</Link>
          <div style={S.user}>
            <div style={S.avatar}>{(user?.name || 'U')[0]}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.uName}>{user?.name || 'User'}</div>
              <div style={S.uRole}>{roleLabel}</div>
            </div>
            <button onClick={doLogout} title="Log out" aria-label="Log out" style={S.iconBtn}><Icon name="power" size={17} /></button>
          </div>
        </div>
      </aside>

      <main style={{ ...S.main, ...(isMobile ? { paddingTop: 54 } : {}) }}><Outlet /></main>
    </div>
  );
}

// Rules inline styles can't express: dark map tiles, themed Leaflet popups,
// hover states, and native form controls inside the fleet shell.
const GLOBAL_CSS = `
/* Inverting a light basemap is a hack — it turns parks pink and roads grey on
   grey — and it is only still here for the raster fallback. A vector basemap
   (.fx-vector) has a real dark style, and satellite (.fx-sat) must never be
   touched at all. */
[data-theme="dark"] .leaflet-container:not(.fx-sat):not(.fx-vector) .leaflet-tile-pane { filter: invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.9) saturate(0.7); }
[data-theme="dark"] .leaflet-container { background: #1C1C1E; }
[data-theme] .leaflet-popup-content-wrapper, [data-theme] .leaflet-popup-tip { background: var(--fx-surface); color: var(--fx-text); box-shadow: var(--fx-shadow-lg); }
[data-theme] .leaflet-bar a { background: var(--fx-surface); color: var(--fx-text); border-color: var(--fx-border); }
[data-theme] nav a:hover, [data-theme] nav button:hover { background: var(--fx-hover); }
[data-theme] tbody tr:hover td { background: var(--fx-hover); }
[data-theme] input, [data-theme] select, [data-theme] textarea { color-scheme: inherit; }
[data-theme] input::placeholder, [data-theme] textarea::placeholder { color: var(--fx-text-3); }
[data-theme] input:focus, [data-theme] select:focus, [data-theme] textarea:focus { border-color: var(--fx-accent) !important; box-shadow: 0 0 0 3px var(--fx-accent-soft); }
`;

const S = css({
  wrap: { display: 'flex', minHeight: '100vh', fontFamily: FONT, fontSize: 13.5, background: 'var(--fx-bg)', color: 'var(--fx-text)', WebkitFontSmoothing: 'antialiased', transition: 'background .25s, color .25s' },
  topbar: { position: 'fixed', top: 0, left: 0, right: 0, height: 54, zIndex: 50, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', background: 'var(--fx-sidebar)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid var(--fx-border)' },
  topName: { flex: 1, fontSize: 16, fontWeight: 600, textAlign: 'center' },
  iconBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, border: 'none', background: 'transparent', color: 'var(--fx-text-2)', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'var(--fx-scrim)', zIndex: 55 },
  // The gradient gives the sidebar some depth instead of one flat panel. Both
  // stops are translucent, so the frosted blur behind it still does its work.
  side: {
    width: 268, position: 'sticky', top: 0, height: '100vh', flexShrink: 0,
    display: 'flex', flexDirection: 'column',
    background: 'linear-gradient(180deg, var(--fx-sidebar) 0%, var(--fx-sidebar-2) 100%)',
    backdropFilter: 'saturate(180%) blur(24px)', WebkitBackdropFilter: 'saturate(180%) blur(24px)',
    borderRight: '1px solid var(--fx-border)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 12, padding: '22px 18px 16px', margin: '0 12px 6px', borderBottom: '1px solid var(--fx-border)' },
  // The mark keeps its own colours in both themes, so it needs its own light
  // plate: #41551F on the dark sidebar would be a green smudge on navy.
  appIcon: { width: 42, height: 42, borderRadius: 11, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FDFCF5', border: '1px solid rgba(65,85,31,0.18)', boxShadow: '0 4px 12px rgba(65,85,31,0.22), inset 0 1px 0 rgba(255,255,255,0.6)' },
  appIconImg: { display: 'block' },
  brandName: { fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.02em' },
  brandSub: { fontSize: 12, color: 'var(--fx-sidebar-text-2)', marginTop: 1 },
  nav: { flex: 1, overflow: 'auto', padding: '4px 12px 12px' },
  // On the tinted sidebar, not on the page — so it uses the sidebar's own
  // muted colour. The page's grey measures 1.7:1 against that blue.
  secTitle: { fontSize: 11.5, fontWeight: 600, letterSpacing: '0.02em', color: 'var(--fx-sidebar-text-3)', padding: '14px 12px 6px' },
  item: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', marginBottom: 2, borderRadius: 10, fontSize: 14.5, fontWeight: 500, color: 'var(--fx-text)', textDecoration: 'none', transition: 'background .12s, color .12s' },
  itemOn: { background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)', fontWeight: 600 },
  tile: { width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.25)' },
  tileSm: { width: 26, height: 26, borderRadius: 7 },
  groupBtn: { width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', font: 'inherit', fontSize: 14.5, textAlign: 'left' },
  subWrap: { display: 'flex', flexDirection: 'column', gap: 1, margin: '2px 0 4px 24px', paddingLeft: 10, borderLeft: '2px solid var(--fx-border)' },
  subItem: { fontSize: 13.5, padding: '6px 10px', gap: 10 },
  bottom: { padding: 14, borderTop: '1px solid var(--fx-border)', display: 'flex', flexDirection: 'column', gap: 10 },
  seg: { display: 'flex', padding: 3, borderRadius: 10, background: 'var(--fx-gray-soft)' },
  segBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 0', border: 'none', borderRadius: 8, background: 'transparent', color: 'var(--fx-text-2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', font: 'inherit', transition: 'all .15s' },
  segOn: { background: 'var(--fx-surface)', color: 'var(--fx-text)', fontWeight: 600, boxShadow: '0 1px 4px rgba(0,0,0,0.14)' },
  back: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 9, fontSize: 13, fontWeight: 500, color: 'var(--fx-text-2)', textDecoration: 'none', background: 'var(--fx-gray-soft)' },
  user: { display: 'flex', alignItems: 'center', gap: 11, padding: '10px', borderRadius: 12, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', boxShadow: 'var(--fx-shadow)' },
  avatar: { width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#fff', background: 'linear-gradient(180deg, #5AB0FF, #007AFF)' },
  uName: { fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  uRole: { fontSize: 12, color: 'var(--fx-text-2)', textTransform: 'capitalize' },
  main: { flex: 1, minWidth: 0, overflow: 'auto', height: '100vh' },
});

